import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { timeout } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

/** El embudo de un link. Espejo de `EmbudoOrigen` del modulo `marketing` de la API. */
export interface EmbudoMarketing {
  origen: string;
  nombre: string;
  clics: number;
  correoVerificado: number;
  registroCompleto: number;
  kycAprobado: number;
  conFondeo: number;
}

/** Una persona que verifico el correo por el link. */
export interface RegistroMarketing {
  origen: string;
  email: string;
  clicAt: string;
  correoVerificadoAt: string | null;
  usuarioId: number | null;
  nombre: string | null;
  estadoCompliance: string | null;
  kycAprobado: boolean;
  fondeo: boolean;
}

/** Lo que queda guardado en el navegador entre el clic y el registro. */
interface VisitaGuardada {
  visitanteId: string;
  origen: string;
  registroEnviado: boolean;
}

const CLAVE = 'aguacate_mkt_visita';

/** Lo que admite `?origen=`. El backend tiene la lista blanca; esto solo filtra basura. */
const FORMATO_ORIGEN = /^[a-z0-9-]{2,30}$/;

/**
 * El link de campana de Marketing: `/auth/dinamic-signup?origen=redes`.
 *
 * Cuenta el clic al llegar y, si esa persona verifica el correo, ata el
 * registro al link. Todo lo de aca es medicion: **nada lanza y nada se
 * espera**. Un fallo de red, un 429 o un navegador que bloquea localStorage
 * pierden el dato de marketing, nunca el registro del cliente.
 *
 * El dato vive en localStorage y no en el estado del wizard porque el link
 * puede apuntar al login: quien llega ahi y toca "Crear cuenta" tiene que
 * seguir atribuido a la campana.
 *
 * Gana el primer link: si el navegador ya llego por uno, otro no lo pisa.
 */
@Injectable({ providedIn: 'root' })
export class MarketingOrigenService {
  private baseUrl = environment.apiNest;

  constructor(private http: HttpClient) {}

  /** Se llama al abrir el login o el registro. Lee `?origen=` de la URL. */
  capturar(origenCrudo: string | null): void {
    try {
      this.capturarOrigen(origenCrudo);
    } catch {
      // Medicion: un navegador raro no puede romper el login ni el registro.
    }
  }

  private capturarOrigen(origenCrudo: string | null): void {
    const origen = (origenCrudo || '').trim().toLowerCase();
    if (!FORMATO_ORIGEN.test(origen)) return;
    if (this.leer()) return;

    const visita: VisitaGuardada = {
      visitanteId: this.nuevoUuid(),
      origen,
      registroEnviado: false,
    };
    // Sin storage no hay como atar el registro despues, y contar el clic
    // igual inflaria el embudo con visitas que nunca pueden convertir.
    if (!this.guardar(visita)) return;

    this.http
      .post(
        `${this.baseUrl}public/marketing/visita`,
        { visitanteId: visita.visitanteId, origen },
        { headers: new HttpHeaders({ 'Content-Type': 'application/json', skip: 'true' }) }
      )
      .subscribe({ error: () => undefined });
  }

  /**
   * Se llama al verificarse el correo, en el paso 3, con la sesion recien
   * creada. El backend toma el correo del token.
   */
  async registrarSiCorresponde(): Promise<void> {
    const visita = this.leer();
    if (!visita || visita.registroEnviado) return;

    try {
      // Tope corto: esto corre dentro del paso 3 y el cliente no puede
      // quedarse esperando por un dato de marketing si la API anda lenta.
      await firstValueFrom(
        this.http
          .post(`${this.baseUrl}marketing/registro`, {
            visitanteId: visita.visitanteId,
            origen: visita.origen,
          })
          .pipe(timeout(5000))
      );
      this.guardar({ ...visita, registroEnviado: true });
    } catch {
      // Se reintenta si el paso 3 vuelve a correr; si no, se pierde el dato.
    }
  }

  // ---------------------------------------------------------------------
  // Panel de operadores
  // ---------------------------------------------------------------------

  /** El link publico de un origen, el que se le entrega a Marketing. */
  linkDe(origen: string): string {
    return `${window.location.origin}/auth/dinamic-signup?origen=${origen}`;
  }

  /**
   * El mismo link en corto (`/r/redes`), para redes y para el QR: un QR con
   * menos caracteres tiene modulos mas grandes y se lee mejor impreso chico.
   * Lo resuelve `linkCampanaGuard`.
   */
  linkCortoDe(origen: string): string {
    return `${window.location.origin}/r/${origen}`;
  }

  embudo(desde?: string, hasta?: string): Promise<EmbudoMarketing[]> {
    return firstValueFrom(
      this.http.get<EmbudoMarketing[]>(`${this.baseUrl}marketing-control/embudo`, {
        params: this.rango(desde, hasta),
      })
    );
  }

  registros(desde?: string, hasta?: string): Promise<RegistroMarketing[]> {
    return firstValueFrom(
      this.http.get<RegistroMarketing[]>(`${this.baseUrl}marketing-control/registros`, {
        params: this.rango(desde, hasta),
      })
    );
  }

  private rango(desde?: string, hasta?: string): HttpParams {
    let params = new HttpParams();
    if (desde) params = params.set('desde', desde);
    if (hasta) params = params.set('hasta', hasta);
    return params;
  }

  // ---------------------------------------------------------------------

  private leer(): VisitaGuardada | null {
    try {
      const crudo = localStorage.getItem(CLAVE);
      const v = crudo ? JSON.parse(crudo) : null;
      return v?.visitanteId && v?.origen ? v : null;
    } catch {
      return null;
    }
  }

  private guardar(v: VisitaGuardada): boolean {
    try {
      localStorage.setItem(CLAVE, JSON.stringify(v));
      return true;
    } catch {
      return false;
    }
  }

  /** UUID v4, que es lo que valida el backend. */
  private nuevoUuid(): string {
    // `randomUUID` solo existe en contexto seguro y en navegadores de 2021 en
    // adelante; `getRandomValues` esta en todos.
    const c: Crypto = window.crypto;
    if (typeof (c as any).randomUUID === 'function') {
      return (c as any).randomUUID();
    }
    const b = c.getRandomValues(new Uint8Array(16));
    b[6] = (b[6] & 0x0f) | 0x40;
    b[8] = (b[8] & 0x3f) | 0x80;
    const h = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
    return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
  }
}
